import { Fragment, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Typography,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  Tooltip,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Chip,
  TextField,
} from 'ui/mui';
import {
  ArrowBack as BackIcon,
  Insights as InsightsIcon,
  SwapHoriz as SwapIcon,
  TrendingUp as TrendingUpIcon,
  WarningAmber as WarningIcon,
} from 'ui/icons';
import { useNavigate } from 'react-router-dom';
import './StreamPerformanceDashboard.css';

function formatMoney(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return '—';
  return `£${n.toFixed(2)}`;
}

function formatNumber(value, digits = 0) {
  const n = Number(value);
  if (!Number.isFinite(n)) return '—';
  return n.toFixed(digits);
}

function formatPct(value, digits = 1) {
  const n = Number(value);
  if (!Number.isFinite(n)) return '—';
  return `${(n * 100).toFixed(digits)}%`;
}

function formatSeconds(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return '—';
  const total = Math.max(0, Math.round(n));
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const seconds = total % 60;
  if (hours > 0) return `${hours}h ${minutes}m ${seconds}s`;
  if (minutes > 0) return `${minutes}m ${seconds}s`;
  return `${seconds}s`;
}

function formatDate(value) {
  if (!value) return '—';
  const raw = String(value).slice(0, 10);
  return raw;
}

function formatDatePretty(value) {
  if (!value) return '—';
  const raw = String(value).slice(0, 10);
  const parts = raw.split('-').map((p) => Number(p));
  if (parts.length !== 3 || parts.some((n) => !Number.isFinite(n))) return raw;
  const [year, month, day] = parts;
  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const monthLabel = monthNames[month - 1] || String(month);
  return `${day} ${monthLabel} ${year}`;
}

function formatDateTime(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toISOString().replace('T', ' ').slice(0, 16) + 'Z';
}

function toSlug(value) {
  return String(value || '')
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

function getTimestamp(stream) {
  const t = new Date(stream?.uploadedAt || stream?.streamDate || 0).getTime();
  return Number.isFinite(t) ? t : 0;
}

function getUserSession() {
  try {
    const raw = localStorage.getItem('user');
    if (!raw) return null;
    const user = JSON.parse(raw);
    if (!user?.accessToken) return null;
    return user;
  } catch (error) {
    return null;
  }
}

const ADMIN_EMAILS = ['randolph@pokerand.net', 'andrew.shane@pokerand.net'];
const STREAM_ACCESS_OWNER_EMAILS = ['randolph@pokerand.net', 'randolph@randcards.com'];

function isAdminSession(session) {
  if (!session) return false;
  const role = String(session.role || '').toLowerCase().trim();
  if (role === 'admin') return true;
  const email = String(session.email || '').toLowerCase().trim();
  return ADMIN_EMAILS.includes(email);
}

function averageMetricRows(rows) {
  if (!rows || rows.length === 0) return null;

  const metricRows = rows.filter((r) => r?.metrics);
  if (metricRows.length === 0) return null;

  const avg = (fn) => metricRows.reduce((acc, r) => acc + Number(fn(r) || 0), 0) / metricRows.length;

  return {
    count: metricRows.length,
    paidAuctionCount: avg((r) => r.metrics.paid_auction_count),
    totalRevenue: avg((r) => r.metrics.total_revenue),
    revenuePerHour: avg((r) => r.metrics.revenue_per_hour),
    avgPaidPrice: avg((r) => r.metrics.avg_paid_price),
    runtimeSeconds: avg((r) => r.metrics.runtime_seconds),
    auctionsPerHour: avg((r) => r.metrics.auctions_per_hour),
    medianGapSeconds: avg((r) => r.metrics.median_gap_seconds),
    trimmedMeanGapSeconds: avg((r) => r.metrics.trimmed_mean_gap_seconds),
    under10: avg((r) => r.metrics.price_bands_json?.under10),
    atLeast20: avg((r) => r.metrics.price_bands_json?.atLeast20),
    atLeast50: avg((r) => r.metrics.price_bands_json?.atLeast50),
    collapseBreaks: avg((r) => r.metrics.break_summary_json?.collapseBreaks),
    breaksRun: avg((r) => r.metrics.break_summary_json?.breaksRun),
    avgBreakDurationSeconds: avg((r) => r.metrics.break_summary_json?.avgBreakDurationSeconds),
    medianResetSeconds: avg((r) => r.metrics.break_summary_json?.medianResetSeconds),
    gradedRuns: avg((r) => r.metrics.break_summary_json?.auctionSummary?.gradedOnScreen?.estimatedRuns),
    gradedAvgRevenuePerRun: avg((r) => r.metrics.break_summary_json?.auctionSummary?.gradedOnScreen?.avgRevenuePerRun),
    singleAvgRevenuePerRun: avg((r) => r.metrics.break_summary_json?.auctionSummary?.singleOnScreen?.avgRevenuePerRun),
    offScreenRevenueShare: avg((r) => r.metrics.break_summary_json?.auctionSummary?.offScreenRevenueShare),
    offScreenAuctionShare: avg((r) => r.metrics.break_summary_json?.auctionSummary?.offScreenAuctionShare),
    singleRuns: avg((r) => r.metrics.break_summary_json?.auctionSummary?.singleOnScreen?.estimatedRuns),
  };
}

// Whether a change reads as better (green), worse (red) or no change (grey). Drawn with data-delta.
function deltaTone(value, inverseGood = false) {
  const n = Number(value);
  if (!Number.isFinite(n)) return 'flat';
  if (Math.abs(n) < 0.0001) return 'flat';
  const positiveIsGood = inverseGood ? n < 0 : n > 0;
  return positiveIsGood ? 'good' : 'bad';
}

function formatDeltaMoney(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return '—';
  const abs = Math.abs(n).toFixed(2);
  return `${n >= 0 ? '+' : '-'}£${abs}`;
}

function formatDeltaPct(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return '—';
  const abs = (Math.abs(n) * 100).toFixed(1);
  return `${n >= 0 ? '+' : '-'}${abs}%`;
}

function formatDeltaNumber(value, digits = 1) {
  const n = Number(value);
  if (!Number.isFinite(n)) return '—';
  const abs = Math.abs(n).toFixed(digits);
  return `${n >= 0 ? '+' : '-'}${abs}`;
}

function formatDeltaSeconds(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return '—';
  const sign = n >= 0 ? '+' : '-';
  return `${sign}${formatSeconds(Math.abs(n))}`;
}

function toNumber(value, fallback = NaN) {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

function isSingleOnlyCategoryKey(categoryKey) {
  const key = String(categoryKey || '');
  return key.includes('single') && !key.includes('slab');
}

function isAuctionCardCategoryKey(categoryKey) {
  const key = String(categoryKey || '');
  return key.includes('slab') || key.includes('single');
}

function getAuctionSummaryFromMetrics(metrics) {
  return metrics?.break_summary_json?.auctionSummary || {};
}

function getPrimaryOnScreenSummary(metrics, categoryKey) {
  const auction = getAuctionSummaryFromMetrics(metrics);
  if (isSingleOnlyCategoryKey(categoryKey)) {
    return auction.singleOnScreen || {};
  }
  return auction.gradedOnScreen || {};
}

function streamMetricHeaderLabel(stream) {
  if (!stream) return 'No stream selected';
  const streamer = stream.streamer?.displayName || 'Unknown';
  const category = stream.breakCategory?.displayName || 'Type';
  const setName = stream.breakSubcategory?.displayName || 'Set';
  const date = formatDatePretty(stream.streamDate || stream.uploadedAt);
  return `${streamer} · ${category} · ${setName} · ${date}`;
}

function streamOptionLabel(stream) {
  if (!stream) return 'Unknown stream';
  const streamer = stream.streamer?.displayName || 'Unknown';
  const category = stream.breakCategory?.displayName || 'Unspecified type';
  const setName = stream.breakSubcategory?.displayName || 'Unspecified set';
  const date = formatDatePretty(stream.streamDate || stream.uploadedAt);
  return `${streamer} · ${category} · ${setName} · ${date} · #${stream.id}`;
}

// One headline figure. The tone (good, warning, bad, neutral) only colours the status icon.
function InsightCard({ title, value, subtitle, tone = 'neutral', icon }) {
  const text = String(value);
  const cut = text.lastIndexOf(' · ');
  return (
    <div className="spd-insight" data-tone={tone}>
      <div className="spd-insight__top">
        <span className="rc-stat__label spd-insight__label">{title}</span>
        <span className="spd-insight__icon">{icon}</span>
      </div>
      <span className="spd-insight__value">
        {cut > 0 ? <>{text.slice(0, cut + 3)}<span className="spd-nowrap">{text.slice(cut + 3)}</span></> : value}
      </span>
      <span className="spd-insight__note">{subtitle}</span>
    </div>
  );
}

export default function StreamPerformanceDashboard() {
  const navigate = useNavigate();

  const [isLoadingLookups, setIsLoadingLookups] = useState(false);
  const [isLoadingReports, setIsLoadingReports] = useState(false);
  const [isDeletingStreamId, setIsDeletingStreamId] = useState('');
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [userRole, setUserRole] = useState('');
  const [canViewPerformance, setCanViewPerformance] = useState(true);
  const [canManagePerformance, setCanManagePerformance] = useState(false);
  const [isResolvingAccess, setIsResolvingAccess] = useState(true);
  const [viewerAccessEntries, setViewerAccessEntries] = useState([]);
  const [isLoadingViewerAccess, setIsLoadingViewerAccess] = useState(false);
  const [viewerEmailInput, setViewerEmailInput] = useState('');
  const [isSavingViewerAccess, setIsSavingViewerAccess] = useState(false);

  const [breakCategories, setBreakCategories] = useState([]);
  const [breakSubcategories, setBreakSubcategories] = useState([]);

  const [selectedBreakCategoryId, setSelectedBreakCategoryId] = useState('');
  const [setScope, setSetScope] = useState('specific'); // specific | all
  const [selectedBreakSubcategoryId, setSelectedBreakSubcategoryId] = useState('');

  const [baselineWindow, setBaselineWindow] = useState(3);
  const [focusStreamerId, setFocusStreamerId] = useState('');
  const [compareStreamAId, setCompareStreamAId] = useState('');
  const [compareStreamBId, setCompareStreamBId] = useState('');

  const [reports, setReports] = useState([]);

  const [detailOpen, setDetailOpen] = useState(false);
  const [detailStream, setDetailStream] = useState(null);
  const [detailBreaks, setDetailBreaks] = useState([]);
  const [isLoadingDetail, setIsLoadingDetail] = useState(false);

  const filteredSubcategories = useMemo(() => {
    if (!selectedBreakCategoryId) return [];
    return breakSubcategories.filter((s) => String(s.breakCategoryId) === String(selectedBreakCategoryId));
  }, [breakSubcategories, selectedBreakCategoryId]);

  const selectedCategory = useMemo(() => {
    return breakCategories.find((c) => String(c.id) === String(selectedBreakCategoryId)) || null;
  }, [breakCategories, selectedBreakCategoryId]);

  const selectedCategoryKey = useMemo(() => {
    return toSlug(selectedCategory?.slug || selectedCategory?.displayName || '');
  }, [selectedCategory]);
  const isAuctionCardCategory = isAuctionCardCategoryKey(selectedCategoryKey);
  const isSingleOnlyCategory = isSingleOnlyCategoryKey(selectedCategoryKey);
  const includeSecondarySingleRows = selectedCategoryKey.includes('single') && selectedCategoryKey.includes('slab');
  const primaryCardSingularLabel = isSingleOnlyCategory ? 'Single' : 'Slab';
  const primaryCardPluralLabel = isSingleOnlyCategory ? 'Singles' : 'Slabs';
  const primaryOnScreenSectionLabel = isSingleOnlyCategory ? 'On-Screen Single Performance' : 'On-Screen Slab Performance';

  const sortedReports = useMemo(() => {
    return [...reports].sort((a, b) => getTimestamp(b) - getTimestamp(a));
  }, [reports]);

  const streamById = useMemo(() => {
    const map = new Map();
    for (const report of sortedReports) {
      map.set(String(report.id), report);
    }
    return map;
  }, [sortedReports]);

  const streamOptions = useMemo(() => {
    return sortedReports.map((r) => ({
      id: String(r.id),
      label: streamOptionLabel(r),
      stream: r,
    }));
  }, [sortedReports]);

  const session = getUserSession();
  const canDeleteStreams = String(userRole).toLowerCase() === 'admin' || isAdminSession(session);

  const latestPerStreamer = useMemo(() => {
    const byStreamer = new Map();
    for (const r of sortedReports) {
      const key = String(r.streamer?.id || 'unknown');
      if (!byStreamer.has(key)) {
        byStreamer.set(key, r);
      }
    }
    return Array.from(byStreamer.values()).sort((a, b) => {
      const an = a.streamer?.displayName || '';
      const bn = b.streamer?.displayName || '';
      return an.localeCompare(bn);
    });
  }, [sortedReports]);

  const peerAverage = useMemo(() => averageMetricRows(latestPerStreamer), [latestPerStreamer]);

  const ownBaselineByStreamer = useMemo(() => {
    const groups = new Map();
    for (const r of sortedReports) {
      const key = String(r.streamer?.id || 'unknown');
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(r);
    }

    const result = new Map();
    for (const [streamerId, rows] of groups.entries()) {
      const latest = rows[0] || null;
      const baselineRows = rows.slice(1, 1 + baselineWindow);
      const baseline = averageMetricRows(baselineRows);
      result.set(streamerId, {
        latest,
        baseline,
        baselineRows,
      });
    }

    return result;
  }, [sortedReports, baselineWindow]);

  const peerRows = useMemo(() => {
    return latestPerStreamer
      .map((stream) => {
        const key = String(stream.streamer?.id || 'unknown');
        const own = ownBaselineByStreamer.get(key);
        const m = stream.metrics;
        const baseline = own?.baseline || null;

        return {
          stream,
          baseline,
          deltaOwn: baseline && m
            ? {
                revenuePerHour: Number(m.revenue_per_hour || 0) - Number(baseline.revenuePerHour || 0),
                under10: Number(m.price_bands_json?.under10 || 0) - Number(baseline.under10 || 0),
                atLeast20: Number(m.price_bands_json?.atLeast20 || 0) - Number(baseline.atLeast20 || 0),
                auctionsPerHour: Number(m.auctions_per_hour || 0) - Number(baseline.auctionsPerHour || 0),
                avgPaidPrice: Number(m.avg_paid_price || 0) - Number(baseline.avgPaidPrice || 0),
              }
            : null,
          deltaPeer: peerAverage && m
            ? {
                revenuePerHour: Number(m.revenue_per_hour || 0) - Number(peerAverage.revenuePerHour || 0),
                under10: Number(m.price_bands_json?.under10 || 0) - Number(peerAverage.under10 || 0),
                atLeast20: Number(m.price_bands_json?.atLeast20 || 0) - Number(peerAverage.atLeast20 || 0),
              }
            : null,
        };
      })
      .sort((a, b) => {
        const aPrimary = getPrimaryOnScreenSummary(a.stream?.metrics, selectedCategoryKey);
        const bPrimary = getPrimaryOnScreenSummary(b.stream?.metrics, selectedCategoryKey);
        const aAuctionScore = Number(aPrimary.avgRevenuePerRun || 0);
        const bAuctionScore = Number(bPrimary.avgRevenuePerRun || 0);
        const av = isAuctionCardCategory && aAuctionScore > 0
          ? aAuctionScore
          : Number(a.stream?.metrics?.revenue_per_hour || 0);
        const bv = isAuctionCardCategory && bAuctionScore > 0
          ? bAuctionScore
          : Number(b.stream?.metrics?.revenue_per_hour || 0);
        return bv - av;
      });
  }, [latestPerStreamer, ownBaselineByStreamer, peerAverage, isAuctionCardCategory, selectedCategoryKey]);

  const insights = useMemo(() => {
    const rows = peerRows.filter((r) => r.stream?.metrics);
    if (rows.length === 0) {
      return {
        gold: null,
        risk: null,
        improved: null,
        declined: null,
      };
    }

    const gold = [...rows].sort((a, b) => {
      const av = isAuctionCardCategory
        ? Number(getPrimaryOnScreenSummary(a.stream.metrics, selectedCategoryKey).avgRevenuePerRun || 0)
        : Number(a.stream.metrics.revenue_per_hour || 0);
      const bv = isAuctionCardCategory
        ? Number(getPrimaryOnScreenSummary(b.stream.metrics, selectedCategoryKey).avgRevenuePerRun || 0)
        : Number(b.stream.metrics.revenue_per_hour || 0);
      return bv - av;
    })[0];

    const risk = [...rows].sort((a, b) => {
      if (isAuctionCardCategory) {
        const av = Number(a.stream.metrics.break_summary_json?.auctionSummary?.offScreenRevenueShare || 0);
        const bv = Number(b.stream.metrics.break_summary_json?.auctionSummary?.offScreenRevenueShare || 0);
        return bv - av;
      }
      const av = Number(a.stream.metrics.price_bands_json?.under10 || 0);
      const bv = Number(b.stream.metrics.price_bands_json?.under10 || 0);
      return bv - av;
    })[0];

    const withOwn = rows.filter((r) => r.deltaOwn && Number.isFinite(r.deltaOwn.revenuePerHour));
    const improved = withOwn.length
      ? [...withOwn].sort((a, b) => Number(b.deltaOwn.revenuePerHour) - Number(a.deltaOwn.revenuePerHour))[0]
      : null;
    const declined = withOwn.length
      ? [...withOwn].sort((a, b) => Number(a.deltaOwn.revenuePerHour) - Number(b.deltaOwn.revenuePerHour))[0]
      : null;

    return { gold, risk, improved, declined };
  }, [peerRows, isAuctionCardCategory, selectedCategoryKey]);

  const availableStreamerOptions = useMemo(() => {
    return latestPerStreamer.map((r) => ({
      id: String(r.streamer?.id || ''),
      name: r.streamer?.displayName || 'Unknown',
    }));
  }, [latestPerStreamer]);

  useEffect(() => {
    let mounted = true;
    const currentSession = getUserSession();
    if (!currentSession?.accessToken) {
      setUserRole('');
      setCanViewPerformance(false);
      setCanManagePerformance(false);
      setIsResolvingAccess(false);
      return () => {
        mounted = false;
      };
    }

    fetch('/api/user', {
      headers: {
        Authorization: `Bearer ${currentSession.accessToken}`,
      },
    })
      .then(async (res) => {
        if (!res.ok) {
          throw new Error(`HTTP ${res.status}`);
        }
        return res.json();
      })
      .then((payload) => {
        if (!mounted) return;
        setUserRole(String(payload?.role || '').toLowerCase());
        const normalizedEmail = String(payload?.email || currentSession.email || '').toLowerCase().trim();
        const canView = Boolean(payload?.permissions?.stream_performance || String(payload?.role || '').toLowerCase() === 'admin');
        const canManage = STREAM_ACCESS_OWNER_EMAILS.includes(normalizedEmail);
        setCanViewPerformance(canView);
        setCanManagePerformance(canManage);
        setIsResolvingAccess(false);

        try {
          const raw = localStorage.getItem('user');
          const stored = raw ? JSON.parse(raw) : null;
          if (stored && stored.accessToken) {
            localStorage.setItem('user', JSON.stringify({
              ...stored,
              role: String(payload?.role || stored.role || '').toLowerCase() || 'staff',
              permissions: payload?.permissions || stored.permissions || null,
            }));
          }
        } catch (storageError) {
          // Ignore local storage sync failures
        }
      })
      .catch(() => {
        if (!mounted) return;
        setUserRole('');
        setCanViewPerformance(false);
        setCanManagePerformance(false);
        setIsResolvingAccess(false);
      });

    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    let mounted = true;
    const currentSession = getUserSession();
    if (!currentSession?.accessToken || !canManagePerformance) {
      setViewerAccessEntries([]);
      return () => {
        mounted = false;
      };
    }

    setIsLoadingViewerAccess(true);
    fetch('/api/stream/access', {
      headers: {
        Authorization: `Bearer ${currentSession.accessToken}`,
      },
    })
      .then(async (res) => {
        const payload = await res.json().catch(() => ({}));
        if (!res.ok) {
          throw new Error(payload?.details || payload?.message || `HTTP ${res.status}`);
        }
        return payload;
      })
      .then((payload) => {
        if (!mounted) return;
        setViewerAccessEntries(Array.isArray(payload?.entries) ? payload.entries : []);
      })
      .catch((fetchError) => {
        if (!mounted) return;
        setError(fetchError.message || 'Failed to load stream access list');
      })
      .finally(() => {
        if (!mounted) return;
        setIsLoadingViewerAccess(false);
      });

    return () => {
      mounted = false;
    };
  }, [canManagePerformance]);

  useEffect(() => {
    if (streamOptions.length === 0) {
      setCompareStreamAId('');
      setCompareStreamBId('');
      return;
    }

    const ids = new Set(streamOptions.map((s) => s.id));
    let nextA = ids.has(compareStreamAId) ? compareStreamAId : streamOptions[0].id;
    let nextB = ids.has(compareStreamBId) ? compareStreamBId : '';

    // Keep user-selected A/B even if they are the same stream. This avoids
    // the previous auto-reset "glitch" when only one stream exists in scope.
    if (!nextB) {
      const fallback = streamOptions.find((s) => s.id !== nextA);
      nextB = fallback ? fallback.id : nextA;
    }

    if (nextA !== compareStreamAId) {
      setCompareStreamAId(nextA);
    }
    if (nextB !== compareStreamBId) {
      setCompareStreamBId(nextB);
    }
  }, [streamOptions, compareStreamAId, compareStreamBId]);

  useEffect(() => {
    if (availableStreamerOptions.length === 0) {
      setFocusStreamerId('');
      return;
    }

    const exists = availableStreamerOptions.some((s) => s.id === focusStreamerId);
    if (!exists) {
      setFocusStreamerId(availableStreamerOptions[0].id);
    }
  }, [availableStreamerOptions, focusStreamerId]);

  const focusHistory = useMemo(() => {
    if (!focusStreamerId) return [];
    return sortedReports.filter((r) => String(r.streamer?.id || '') === String(focusStreamerId));
  }, [sortedReports, focusStreamerId]);

  const focusCurrent = focusHistory[0] || null;
  const focusBaseline = useMemo(() => {
    if (!focusHistory.length) return null;
    return averageMetricRows(focusHistory.slice(1, 1 + baselineWindow));
  }, [focusHistory, baselineWindow]);

  const compareStreamA = compareStreamAId ? streamById.get(compareStreamAId) || null : null;
  const compareStreamB = compareStreamBId ? streamById.get(compareStreamBId) || null : null;

  const specificComparisonRows = useMemo(() => {
    const a = compareStreamA?.metrics;
    const b = compareStreamB?.metrics;
    if (!a || !b) return [];

    const aAuction = a.break_summary_json?.auctionSummary || {};
    const bAuction = b.break_summary_json?.auctionSummary || {};
    const aPrimary = getPrimaryOnScreenSummary(a, selectedCategoryKey);
    const bPrimary = getPrimaryOnScreenSummary(b, selectedCategoryKey);
    const aSingle = aAuction.singleOnScreen || {};
    const bSingle = bAuction.singleOnScreen || {};
    const aOff = aAuction.nonOnScreen || {};
    const bOff = bAuction.nonOnScreen || {};
    const aClean = a.break_summary_json?.cleaningSummary || {};
    const bClean = b.break_summary_json?.cleaningSummary || {};

    const commonRows = [
      {
        key: 'revHr',
        section: 'Revenue & Output',
        label: 'Revenue per hour',
        a: toNumber(a.revenue_per_hour, 0),
        b: toNumber(b.revenue_per_hour, 0),
        format: formatMoney,
        deltaFormatter: formatDeltaMoney,
        inverseGood: false,
      },
      {
        key: 'revenue',
        section: 'Revenue & Output',
        label: 'Total revenue',
        a: toNumber(a.total_revenue, 0),
        b: toNumber(b.total_revenue, 0),
        format: formatMoney,
        deltaFormatter: formatDeltaMoney,
        inverseGood: false,
      },
      {
        key: 'avgWin',
        section: 'Revenue & Output',
        label: 'Average winning price',
        a: toNumber(a.avg_paid_price, 0),
        b: toNumber(b.avg_paid_price, 0),
        format: formatMoney,
        deltaFormatter: formatDeltaMoney,
        inverseGood: false,
      },
      {
        key: 'paidAuctions',
        section: 'Revenue & Output',
        label: 'Paid auctions',
        a: toNumber(a.paid_auction_count, 0),
        b: toNumber(b.paid_auction_count, 0),
        format: (value) => formatNumber(value, 0),
        deltaFormatter: (value) => formatDeltaNumber(value, 0),
        inverseGood: false,
      },
      {
        key: 'auctionsHr',
        section: 'Revenue & Output',
        label: 'Auctions per hour',
        a: toNumber(a.auctions_per_hour, 0),
        b: toNumber(b.auctions_per_hour, 0),
        format: (value) => formatNumber(value, 1),
        deltaFormatter: (value) => formatDeltaNumber(value, 1),
        inverseGood: false,
      },
      {
        key: 'runtime',
        section: 'Pace & Timing',
        label: 'Runtime (paid auctions)',
        a: toNumber(a.runtime_seconds, 0),
        b: toNumber(b.runtime_seconds, 0),
        format: formatSeconds,
        deltaFormatter: formatDeltaSeconds,
        inverseGood: true,
      },
      {
        key: 'medianGap',
        section: 'Pace & Timing',
        label: 'Median auction gap',
        a: toNumber(a.median_gap_seconds, 0),
        b: toNumber(b.median_gap_seconds, 0),
        format: formatSeconds,
        deltaFormatter: formatDeltaSeconds,
        inverseGood: true,
      },
      {
        key: 'trimmedGap',
        section: 'Pace & Timing',
        label: 'Trimmed mean gap (5-95%)',
        a: toNumber(a.trimmed_mean_gap_seconds, 0),
        b: toNumber(b.trimmed_mean_gap_seconds, 0),
        format: formatSeconds,
        deltaFormatter: formatDeltaSeconds,
        inverseGood: true,
      },
      {
        key: 'under10',
        section: 'Price Architecture',
        label: 'Under £10 rate',
        a: toNumber(a.price_bands_json?.under10, 0),
        b: toNumber(b.price_bands_json?.under10, 0),
        format: formatPct,
        deltaFormatter: formatDeltaPct,
        inverseGood: true,
      },
      {
        key: 'atLeast20',
        section: 'Price Architecture',
        label: '£20+ rate',
        a: toNumber(a.price_bands_json?.atLeast20, 0),
        b: toNumber(b.price_bands_json?.atLeast20, 0),
        format: formatPct,
        deltaFormatter: formatDeltaPct,
        inverseGood: false,
      },
      {
        key: 'atLeast50',
        section: 'Price Architecture',
        label: '£50+ rate',
        a: toNumber(a.price_bands_json?.atLeast50, 0),
        b: toNumber(b.price_bands_json?.atLeast50, 0),
        format: formatPct,
        deltaFormatter: formatDeltaPct,
        inverseGood: false,
      },
    ];

    const primaryRows = [
      {
        key: 'primaryRuns',
        section: primaryOnScreenSectionLabel,
        label: `${primaryCardPluralLabel} ran (# on screen)`,
        a: toNumber(aPrimary.estimatedRuns, 0),
        b: toNumber(bPrimary.estimatedRuns, 0),
        format: (value) => formatNumber(value, 0),
        deltaFormatter: (value) => formatDeltaNumber(value, 0),
        inverseGood: false,
      },
      {
        key: 'primaryRevenue',
        section: primaryOnScreenSectionLabel,
        label: `${primaryCardSingularLabel} on-screen revenue`,
        a: toNumber(aPrimary.revenue, 0),
        b: toNumber(bPrimary.revenue, 0),
        format: formatMoney,
        deltaFormatter: formatDeltaMoney,
        inverseGood: false,
      },
      {
        key: 'primaryAvgPerRun',
        section: primaryOnScreenSectionLabel,
        label: `Avg revenue per ${primaryCardSingularLabel.toLowerCase()} run`,
        a: toNumber(aPrimary.avgRevenuePerRun, 0),
        b: toNumber(bPrimary.avgRevenuePerRun, 0),
        format: formatMoney,
        deltaFormatter: formatDeltaMoney,
        inverseGood: false,
      },
      {
        key: 'primaryAvgPerAuction',
        section: primaryOnScreenSectionLabel,
        label: `Avg revenue per ${primaryCardSingularLabel.toLowerCase()} auction`,
        a: toNumber(aPrimary.avgRevenuePerAuction, 0),
        b: toNumber(bPrimary.avgRevenuePerAuction, 0),
        format: formatMoney,
        deltaFormatter: formatDeltaMoney,
        inverseGood: false,
      },
      {
        key: 'primaryAuctionsHr',
        section: primaryOnScreenSectionLabel,
        label: `${primaryCardSingularLabel} auctions per hour`,
        a: toNumber(aPrimary.auctionsPerHour, 0),
        b: toNumber(bPrimary.auctionsPerHour, 0),
        format: (value) => formatNumber(value, 1),
        deltaFormatter: (value) => formatDeltaNumber(value, 1),
        inverseGood: false,
      },
      {
        key: 'primaryMedianGap',
        section: primaryOnScreenSectionLabel,
        label: `${primaryCardSingularLabel} median auction gap`,
        a: toNumber(aPrimary.medianGapSeconds, 0),
        b: toNumber(bPrimary.medianGapSeconds, 0),
        format: formatSeconds,
        deltaFormatter: formatDeltaSeconds,
        inverseGood: true,
      },
      {
        key: 'primaryTrimmedGap',
        section: primaryOnScreenSectionLabel,
        label: `${primaryCardSingularLabel} trimmed gap (5-95%)`,
        a: toNumber(aPrimary.trimmedMeanGapSeconds, 0),
        b: toNumber(bPrimary.trimmedMeanGapSeconds, 0),
        format: formatSeconds,
        deltaFormatter: formatDeltaSeconds,
        inverseGood: true,
      },
      {
        key: 'offAuctionCount',
        section: 'Off-Screen / BIN',
        label: 'Off-screen paid auctions',
        a: toNumber(aOff.auctionCount, 0),
        b: toNumber(bOff.auctionCount, 0),
        format: (value) => formatNumber(value, 0),
        deltaFormatter: (value) => formatDeltaNumber(value, 0),
        inverseGood: true,
      },
      {
        key: 'offRevenue',
        section: 'Off-Screen / BIN',
        label: 'Off-screen paid revenue',
        a: toNumber(aOff.revenue, 0),
        b: toNumber(bOff.revenue, 0),
        format: formatMoney,
        deltaFormatter: formatDeltaMoney,
        inverseGood: true,
      },
      {
        key: 'offRevenueShare',
        section: 'Off-Screen / BIN',
        label: 'Off-screen revenue share',
        a: toNumber(aAuction.offScreenRevenueShare, 0),
        b: toNumber(bAuction.offScreenRevenueShare, 0),
        format: formatPct,
        deltaFormatter: formatDeltaPct,
        inverseGood: true,
      },
    ];

    const singleRows = includeSecondarySingleRows ? [
      {
        key: 'singleRuns',
        section: 'Single On-Screen Performance',
        label: 'Single-card runs (# on screen)',
        a: toNumber(aSingle.estimatedRuns, 0),
        b: toNumber(bSingle.estimatedRuns, 0),
        format: (value) => formatNumber(value, 0),
        deltaFormatter: (value) => formatDeltaNumber(value, 0),
        inverseGood: false,
      },
      {
        key: 'singleAvgPerRun',
        section: 'Single On-Screen Performance',
        label: 'Avg revenue per single-card run',
        a: toNumber(aSingle.avgRevenuePerRun, 0),
        b: toNumber(bSingle.avgRevenuePerRun, 0),
        format: formatMoney,
        deltaFormatter: formatDeltaMoney,
        inverseGood: false,
      },
    ] : [];

    const cleaningRows = [
      {
        key: 'giveawaysRemoved',
        section: 'Data Hygiene',
        label: 'Giveaways removed (£0 successful rows)',
        a: toNumber(aClean.giveawaysRemoved, 0),
        b: toNumber(bClean.giveawaysRemoved, 0),
        format: (value) => formatNumber(value, 0),
        deltaFormatter: (value) => formatDeltaNumber(value, 0),
        inverseGood: true,
      },
      {
        key: 'cancelledRemoved',
        section: 'Data Hygiene',
        label: 'Failed/cancelled rows removed',
        a: toNumber(aClean.cancelledRemoved, 0),
        b: toNumber(bClean.cancelledRemoved, 0),
        format: (value) => formatNumber(value, 0),
        deltaFormatter: (value) => formatDeltaNumber(value, 0),
        inverseGood: true,
      },
      {
        key: 'totalRows',
        section: 'Data Hygiene',
        label: 'Raw CSV rows',
        a: toNumber(aClean.totalRows, 0),
        b: toNumber(bClean.totalRows, 0),
        format: (value) => formatNumber(value, 0),
        deltaFormatter: (value) => formatDeltaNumber(value, 0),
        inverseGood: false,
      },
    ];

    const breakRows = [
      {
        key: 'collapseBreaks',
        section: 'Break Health',
        label: 'Collapse breaks',
        a: toNumber(a.break_summary_json?.collapseBreaks, 0),
        b: toNumber(b.break_summary_json?.collapseBreaks, 0),
        format: (value) => formatNumber(value, 0),
        deltaFormatter: (value) => formatDeltaNumber(value, 0),
        inverseGood: true,
      },
      {
        key: 'breaksRun',
        section: 'Break Health',
        label: 'Breaks run',
        a: toNumber(a.break_summary_json?.breaksRun, 0),
        b: toNumber(b.break_summary_json?.breaksRun, 0),
        format: (value) => formatNumber(value, 0),
        deltaFormatter: (value) => formatDeltaNumber(value, 0),
        inverseGood: false,
      },
      {
        key: 'avgBreakDuration',
        section: 'Break Health',
        label: 'Avg break duration',
        a: toNumber(a.break_summary_json?.avgBreakDurationSeconds, 0),
        b: toNumber(b.break_summary_json?.avgBreakDurationSeconds, 0),
        format: formatSeconds,
        deltaFormatter: formatDeltaSeconds,
        inverseGood: true,
      },
      {
        key: 'medianReset',
        section: 'Break Health',
        label: 'Median reset between breaks',
        a: toNumber(a.break_summary_json?.medianResetSeconds, 0),
        b: toNumber(b.break_summary_json?.medianResetSeconds, 0),
        format: formatSeconds,
        deltaFormatter: formatDeltaSeconds,
        inverseGood: true,
      },
    ];

    return [
      ...commonRows,
      ...(isAuctionCardCategory ? [...primaryRows, ...singleRows] : breakRows),
      ...cleaningRows,
    ];
  }, [compareStreamA, compareStreamB, includeSecondarySingleRows, isAuctionCardCategory, primaryCardPluralLabel, primaryCardSingularLabel, primaryOnScreenSectionLabel, selectedCategoryKey]);

  const comparisonSections = useMemo(() => {
    const order = isAuctionCardCategory
      ? [
          'Revenue & Output',
          'Pace & Timing',
          primaryOnScreenSectionLabel,
          ...(includeSecondarySingleRows ? ['Single On-Screen Performance'] : []),
          'Off-Screen / BIN',
          'Price Architecture',
          'Data Hygiene',
        ]
      : ['Revenue & Output', 'Pace & Timing', 'Price Architecture', 'Break Health', 'Data Hygiene'];
    return order
      .map((section) => ({
        section,
        rows: specificComparisonRows.filter((row) => row.section === section),
      }))
      .filter((group) => group.rows.length > 0);
  }, [specificComparisonRows, includeSecondarySingleRows, isAuctionCardCategory, primaryOnScreenSectionLabel]);

  const comparisonHighlights = useMemo(() => {
    const pick = (key) => specificComparisonRows.find((row) => row.key === key) || null;
    const rev = pick('revHr');
    const primaryAvg = pick('primaryAvgPerRun');
    const offShare = pick('offRevenueShare');
    const medGap = pick('medianGap');
    const under10 = pick('under10');
    const reset = pick('medianReset');
    const formatHighlight = (row) => {
      if (!row) return '—';
      const delta = toNumber(row.a, 0) - toNumber(row.b, 0);
      const formatter = row.deltaFormatter || formatDeltaNumber;
      return formatter(delta);
    };

    if (isAuctionCardCategory) {
      return [
        { key: 'h1', title: 'Revenue/hr Gap', value: formatHighlight(rev), inverseGood: false, delta: rev ? toNumber(rev.a, 0) - toNumber(rev.b, 0) : NaN },
        { key: 'h2', title: `Avg £/${primaryCardSingularLabel} Gap`, value: formatHighlight(primaryAvg), inverseGood: false, delta: primaryAvg ? toNumber(primaryAvg.a, 0) - toNumber(primaryAvg.b, 0) : NaN },
        { key: 'h3', title: 'Off-screen Rev Gap', value: formatHighlight(offShare), inverseGood: true, delta: offShare ? toNumber(offShare.a, 0) - toNumber(offShare.b, 0) : NaN },
        { key: 'h4', title: 'Median Gap', value: formatHighlight(medGap), inverseGood: true, delta: medGap ? toNumber(medGap.a, 0) - toNumber(medGap.b, 0) : NaN },
      ];
    }

    return [
      { key: 'h1', title: 'Revenue/hr Gap', value: formatHighlight(rev), inverseGood: false, delta: rev ? toNumber(rev.a, 0) - toNumber(rev.b, 0) : NaN },
      { key: 'h2', title: 'Under £10 Gap', value: formatHighlight(under10), inverseGood: true, delta: under10 ? toNumber(under10.a, 0) - toNumber(under10.b, 0) : NaN },
      { key: 'h3', title: 'Median Gap', value: formatHighlight(medGap), inverseGood: true, delta: medGap ? toNumber(medGap.a, 0) - toNumber(medGap.b, 0) : NaN },
      { key: 'h4', title: 'Reset Gap', value: formatHighlight(reset), inverseGood: true, delta: reset ? toNumber(reset.a, 0) - toNumber(reset.b, 0) : NaN },
    ];
  }, [primaryCardSingularLabel, specificComparisonRows, isAuctionCardCategory]);

  const latestByStreamerAndSet = useMemo(() => {
    const map = new Map();
    for (const r of sortedReports) {
      const streamerId = String(r.streamer?.id || 'unknown');
      const setId = String(r.breakSubcategory?.id || 'none');
      const key = `${streamerId}|${setId}`;
      if (!map.has(key)) {
        map.set(key, r);
      }
    }
    return map;
  }, [sortedReports]);

  const setMatrixRows = useMemo(() => {
    if (setScope !== 'all') return [];

    const setMap = new Map();
    for (const r of sortedReports) {
      const sid = String(r.breakSubcategory?.id || 'none');
      if (!setMap.has(sid)) {
        setMap.set(sid, {
          id: sid,
          name: r.breakSubcategory?.displayName || 'Unspecified',
        });
      }
    }

    const streamers = availableStreamerOptions;

    const rows = Array.from(setMap.values()).map((setMeta) => {
      const cells = streamers.map((s) => {
        const stream = latestByStreamerAndSet.get(`${s.id}|${setMeta.id}`) || null;
        const auction = stream?.metrics?.break_summary_json?.auctionSummary || {};
        const primary = getPrimaryOnScreenSummary(stream?.metrics, selectedCategoryKey);
        return {
          streamerId: s.id,
          streamerName: s.name,
          stream,
          revenuePerHour: stream?.metrics?.revenue_per_hour ?? null,
          under10: stream?.metrics?.price_bands_json?.under10 ?? null,
          atLeast20: stream?.metrics?.price_bands_json?.atLeast20 ?? null,
          primaryAvgRevenuePerRun: primary?.avgRevenuePerRun ?? null,
          primaryRuns: primary?.estimatedRuns ?? null,
          offScreenRevenueShare: auction?.offScreenRevenueShare ?? null,
        };
      });

      const valid = cells.filter((c) => c.stream && c.stream.metrics);
      const best = valid.length
        ? [...valid].sort((a, b) => {
          const av = isAuctionCardCategory
            ? Number(a.primaryAvgRevenuePerRun || 0)
            : Number(a.revenuePerHour || 0);
          const bv = isAuctionCardCategory
            ? Number(b.primaryAvgRevenuePerRun || 0)
            : Number(b.revenuePerHour || 0);
          return bv - av;
        })[0]
        : null;
      const highestRisk = valid.length
        ? [...valid].sort((a, b) => {
          const av = isAuctionCardCategory
            ? Number(a.offScreenRevenueShare || 0)
            : Number(a.under10 || 0);
          const bv = isAuctionCardCategory
            ? Number(b.offScreenRevenueShare || 0)
            : Number(b.under10 || 0);
          return bv - av;
        })[0]
        : null;

      return {
        setId: setMeta.id,
        setName: setMeta.name,
        cells,
        best,
        highestRisk,
      };
    });

    return rows.sort((a, b) => a.setName.localeCompare(b.setName));
  }, [setScope, sortedReports, availableStreamerOptions, latestByStreamerAndSet, isAuctionCardCategory, selectedCategoryKey]);

  useEffect(() => {
    let mounted = true;

    const loadLookups = async () => {
      const session = getUserSession();
      if (!session) return;
      if (isResolvingAccess) return;
      if (!canViewPerformance) return;

      setIsLoadingLookups(true);
      setError('');
      try {
        const response = await fetch('/api/stream/lookups', {
          headers: {
            Authorization: `Bearer ${session.accessToken}`,
          },
        });

        if (!response.ok) {
          throw new Error(`Failed to load lookups (${response.status})`);
        }

        const payload = await response.json();
        if (!mounted) return;

        const categories = Array.isArray(payload.breakCategories) ? payload.breakCategories : [];
        const subcategories = Array.isArray(payload.breakSubcategories) ? payload.breakSubcategories : [];

        setBreakCategories(categories);
        setBreakSubcategories(subcategories);

        const defaultCategory = categories.find((c) => c.slug === 'break') || categories[0];
        if (!selectedBreakCategoryId && defaultCategory) {
          const defaultCatId = String(defaultCategory.id);
          setSelectedBreakCategoryId(defaultCatId);

          const related = subcategories.filter(
            (s) => String(s.breakCategoryId) === defaultCatId
          );
          const defaultSub = related.find((s) => s.slug === 'ascended-heroes') || related[0];
          if (defaultSub) {
            setSelectedBreakSubcategoryId(String(defaultSub.id));
          }
        }
      } catch (lookupErr) {
        console.error(lookupErr);
        if (mounted) setError(lookupErr.message || 'Failed to load lookups');
      } finally {
        if (mounted) setIsLoadingLookups(false);
      }
    };

    loadLookups();
    return () => {
      mounted = false;
    };
  }, [canViewPerformance, isResolvingAccess, selectedBreakCategoryId]);

  useEffect(() => {
    let mounted = true;

    const loadReports = async () => {
      const session = getUserSession();
      if (!session) return;
      if (isResolvingAccess) return;
      if (!canViewPerformance) return;
      if (!selectedBreakCategoryId) return;
      if (setScope === 'specific' && !selectedBreakSubcategoryId) return;

      setIsLoadingReports(true);
      setError('');
      try {
        const params = new URLSearchParams();
        params.set('breakCategoryId', String(selectedBreakCategoryId));
        params.set('limit', '200');
        if (setScope === 'specific') {
          params.set('breakSubcategoryId', String(selectedBreakSubcategoryId));
        }

        const response = await fetch(`/api/stream/reports?${params.toString()}`, {
          headers: {
            Authorization: `Bearer ${session.accessToken}`,
          },
        });

        if (!response.ok) {
          const body = await response.json().catch(() => ({}));
          throw new Error(body?.message || `Failed to load reports (${response.status})`);
        }

        const payload = await response.json();
        if (!mounted) return;

        setReports(Array.isArray(payload.streams) ? payload.streams : []);
      } catch (reportErr) {
        console.error(reportErr);
        if (mounted) setError(reportErr.message || 'Failed to load stream reports');
      } finally {
        if (mounted) setIsLoadingReports(false);
      }
    };

    loadReports();
    return () => {
      mounted = false;
    };
  }, [canViewPerformance, isResolvingAccess, selectedBreakCategoryId, selectedBreakSubcategoryId, setScope]);

  const openDetail = async (stream) => {
    const session = getUserSession();
    if (!session) return;

    setDetailOpen(true);
    setDetailStream(stream);
    setDetailBreaks([]);
    setIsLoadingDetail(true);

    try {
      const streamCategoryKey = toSlug(stream?.breakCategory?.slug || stream?.breakCategory?.displayName || '');
      if (isAuctionCardCategoryKey(streamCategoryKey)) {
        setIsLoadingDetail(false);
        return;
      }

      const params = new URLSearchParams();
      params.set('streamUploadId', String(stream.id));

      const response = await fetch(`/api/stream/breaks?${params.toString()}`, {
        headers: {
          Authorization: `Bearer ${session.accessToken}`,
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to load break metrics (${response.status})`);
      }

      const payload = await response.json();
      setDetailBreaks(Array.isArray(payload.breaks) ? payload.breaks : []);
    } catch (detailErr) {
      console.error(detailErr);
      setError(detailErr.message || 'Failed to load break details');
    } finally {
      setIsLoadingDetail(false);
    }
  };

  const deleteStream = async (stream) => {
    const currentSession = getUserSession();
    if (!currentSession) {
      setError('Your session has expired. Please sign in again.');
      return;
    }

    if (!isAdminSession(currentSession)) {
      setError('Only admins can delete streams.');
      return;
    }

    const streamId = Number(stream?.id);
    if (!Number.isInteger(streamId) || streamId <= 0) {
      setError('Invalid stream id.');
      return;
    }

    const confirmPhrase = `DELETE ${streamId}`;
    const typed = window.prompt(
      `Type "${confirmPhrase}" to permanently delete this stream and all derived metrics.`
    );

    if (typed !== confirmPhrase) {
      return;
    }

    setIsDeletingStreamId(String(streamId));
    setError('');
    setSuccessMessage('');

    try {
      const response = await fetch('/api/stream/delete', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${currentSession.accessToken}`,
        },
        body: JSON.stringify({
          streamUploadId: streamId,
        }),
      });

      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(payload?.message || `Failed to delete stream (${response.status})`);
      }

      setReports((prev) => prev.filter((row) => String(row.id) !== String(streamId)));
      setSuccessMessage(payload?.message || `Deleted stream #${streamId}.`);

      if (payload?.storageWarning) {
        setError(`Stream deleted, but file cleanup warning: ${payload.storageWarning}`);
      }
    } catch (deleteErr) {
      setError(deleteErr.message || 'Failed to delete stream.');
    } finally {
      setIsDeletingStreamId('');
    }
  };

  const addViewerAccess = async () => {
    const currentSession = getUserSession();
    if (!currentSession?.accessToken) {
      setError('Your session has expired. Please sign in again.');
      return;
    }
    if (!canManagePerformance) {
      setError('Only Randolph owner accounts can manage stream performance access.');
      return;
    }

    const email = String(viewerEmailInput || '').toLowerCase().trim();
    if (!email) {
      setError('Enter an email to grant access.');
      return;
    }

    setIsSavingViewerAccess(true);
    setError('');
    setSuccessMessage('');
    try {
      const response = await fetch('/api/stream/access', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${currentSession.accessToken}`,
        },
        body: JSON.stringify({ email }),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(payload?.details || payload?.message || `Failed to grant access (${response.status})`);
      }

      setViewerAccessEntries(Array.isArray(payload?.entries) ? payload.entries : []);
      setViewerEmailInput('');
      setSuccessMessage(payload?.message || `Granted access to ${email}.`);
    } catch (addError) {
      setError(addError.message || 'Failed to grant stream performance access.');
    } finally {
      setIsSavingViewerAccess(false);
    }
  };

  const removeViewerAccess = async (email) => {
    const currentSession = getUserSession();
    if (!currentSession?.accessToken) {
      setError('Your session has expired. Please sign in again.');
      return;
    }
    if (!canManagePerformance) {
      setError('Only Randolph owner accounts can manage stream performance access.');
      return;
    }

    const normalizedEmail = String(email || '').toLowerCase().trim();
    if (!normalizedEmail) return;

    setIsSavingViewerAccess(true);
    setError('');
    setSuccessMessage('');
    try {
      const response = await fetch('/api/stream/access', {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${currentSession.accessToken}`,
        },
        body: JSON.stringify({ email: normalizedEmail }),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(payload?.details || payload?.message || `Failed to remove access (${response.status})`);
      }

      setViewerAccessEntries(Array.isArray(payload?.entries) ? payload.entries : []);
      setSuccessMessage(payload?.message || `Removed access for ${normalizedEmail}.`);
    } catch (removeError) {
      setError(removeError.message || 'Failed to remove stream performance access.');
    } finally {
      setIsSavingViewerAccess(false);
    }
  };

  const header = (
    <header className="rc-page-header spd-header">
      <div className="rc-page-header__text">
        <IconButton onClick={() => navigate('/dashboard')} className="spd-back">
          <BackIcon size={18} />
        </IconButton>
        <p className="rc-eyebrow">Streams</p>
        <h1 className="rc-page-title">Stream Performance Manager View</h1>
        <p className="rc-page-description">
          Compare latest stream vs own history and vs peers. Switch to All Sets to benchmark across subcategories.
        </p>
      </div>
    </header>
  );

  const filterBar = (
    <section className="rc-panel spd-filters">
      <FormControl size="small">
        <InputLabel>Category</InputLabel>
        <Select
          value={selectedBreakCategoryId}
          label="Category"
          onChange={(e) => {
            setSelectedBreakCategoryId(String(e.target.value));
            setSelectedBreakSubcategoryId('');
          }}
        >
          {breakCategories.map((c) => (
            <MenuItem key={c.id} value={String(c.id)}>{c.displayName}</MenuItem>
          ))}
        </Select>
      </FormControl>

      <FormControl size="small">
        <InputLabel>Set Scope</InputLabel>
        <Select
          value={setScope}
          label="Set Scope"
          onChange={(e) => setSetScope(String(e.target.value))}
        >
          <MenuItem value="specific">Specific Set</MenuItem>
          <MenuItem value="all">All Sets</MenuItem>
        </Select>
      </FormControl>

      <FormControl size="small" disabled={setScope !== 'specific'}>
        <InputLabel>Subcategory / Set</InputLabel>
        <Select
          value={selectedBreakSubcategoryId}
          label="Subcategory / Set"
          onChange={(e) => setSelectedBreakSubcategoryId(String(e.target.value))}
        >
          {filteredSubcategories.map((s) => (
            <MenuItem key={s.id} value={String(s.id)}>{s.displayName}</MenuItem>
          ))}
        </Select>
      </FormControl>

      <FormControl size="small">
        <InputLabel>Baseline Window</InputLabel>
        <Select
          value={String(baselineWindow)}
          label="Baseline Window"
          onChange={(e) => setBaselineWindow(Number(e.target.value))}
        >
          <MenuItem value="3">Last 3 streams</MenuItem>
          <MenuItem value="5">Last 5 streams</MenuItem>
          <MenuItem value="10">Last 10 streams</MenuItem>
        </Select>
      </FormControl>

      <FormControl size="small">
        <InputLabel>Focus Streamer</InputLabel>
        <Select
          value={focusStreamerId}
          label="Focus Streamer"
          onChange={(e) => setFocusStreamerId(String(e.target.value))}
        >
          {availableStreamerOptions.map((s) => (
            <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>
          ))}
        </Select>
      </FormControl>

      <div className="spd-filters__status">
        {(isLoadingLookups || isLoadingReports) ? <CircularProgress size={18} /> : null}
        <span>
          {latestPerStreamer.length} streamer(s), {sortedReports.length} stream(s)
        </span>
      </div>
    </section>
  );

  const accessManagerPanel = canManagePerformance ? (
    <section className="rc-panel spd-access">
      <div className="spd-panel-head">
        <h2 className="rc-panel__title">
          Stream Performance Access Manager
        </h2>
        <p className="rc-panel__meta">
          Owner-only control. Add any `@pokerand.net` or `@randcards.com` email and they will be able to open this tool after login.
        </p>
      </div>

      <div className="spd-access__form">
        <TextField
          size="small"
          placeholder="user@pokerand.net"
          value={viewerEmailInput}
          onChange={(e) => setViewerEmailInput(e.target.value)}
        />
        <Button
          variant="contained"
          disabled={isSavingViewerAccess || !viewerEmailInput.trim()}
          onClick={addViewerAccess}
        >
          {isSavingViewerAccess ? 'Saving...' : 'Grant Access'}
        </Button>
      </div>

      <p className="spd-note">
        Owner accounts with manage access: {STREAM_ACCESS_OWNER_EMAILS.join(', ')}
      </p>

      <div className="spd-access__viewers">
        {isLoadingViewerAccess ? <CircularProgress size={18} /> : null}
        {!isLoadingViewerAccess && viewerAccessEntries.length === 0 ? (
          <p className="spd-note">
            No extra viewers granted yet.
          </p>
        ) : null}
        {viewerAccessEntries.map((entry) => {
          const email = String(entry?.email || '').toLowerCase();
          const isOwner = STREAM_ACCESS_OWNER_EMAILS.includes(email);
          return (
            <Chip
              key={email}
              label={email}
              onDelete={isOwner ? undefined : () => removeViewerAccess(email)}
              className="spd-viewer"
              data-owner={isOwner ? 'true' : undefined}
            />
          );
        })}
      </div>
    </section>
  ) : null;

  const insightsRow = (
    <div className="spd-insights">
      <InsightCard
        title={isAuctionCardCategory ? `Gold Standard (£/${primaryCardSingularLabel})` : 'Gold Standard (Rev/hr)'}
        value={insights.gold
          ? `${insights.gold.stream.streamer?.displayName} · ${
            isAuctionCardCategory
              ? formatMoney(getPrimaryOnScreenSummary(insights.gold.stream.metrics, selectedCategoryKey).avgRevenuePerRun)
              : formatMoney(insights.gold.stream.metrics.revenue_per_hour)
          }`
          : '—'}
        subtitle={insights.gold ? `Stream #${insights.gold.stream.id} · ${formatDate(insights.gold.stream.streamDate)}` : 'No data'}
        tone="good"
        icon={<TrendingUpIcon />}
      />
      <InsightCard
        title={isAuctionCardCategory ? 'Highest Off-screen Share' : 'Highest Floor Risk'}
        value={insights.risk
          ? `${insights.risk.stream.streamer?.displayName} · ${
            isAuctionCardCategory
              ? formatPct(insights.risk.stream.metrics.break_summary_json?.auctionSummary?.offScreenRevenueShare)
              : formatPct(insights.risk.stream.metrics.price_bands_json?.under10)
          }`
          : '—'}
        subtitle={isAuctionCardCategory ? 'Off-screen/BIN share of paid revenue' : 'Under £10 paid auctions'}
        tone="bad"
        icon={<WarningIcon />}
      />
      <InsightCard
        title="Most Improved vs Own"
        value={insights.improved ? `${insights.improved.stream.streamer?.displayName} · ${formatDeltaMoney(insights.improved.deltaOwn?.revenuePerHour)}` : '—'}
        subtitle={`Rev/hr vs own last ${baselineWindow}`}
        tone={insights.improved ? 'good' : 'neutral'}
        icon={<InsightsIcon />}
      />
      <InsightCard
        title="Largest Decline vs Own"
        value={insights.declined ? `${insights.declined.stream.streamer?.displayName} · ${formatDeltaMoney(insights.declined.deltaOwn?.revenuePerHour)}` : '—'}
        subtitle={`Rev/hr vs own last ${baselineWindow}`}
        tone={insights.declined ? 'warning' : 'neutral'}
        icon={<InsightsIcon />}
      />
    </div>
  );

  const specificComparisonPanel = (
    <section className="rc-panel spd-compare">
      <div className="spd-panel-head">
        <h2 className="rc-panel__title">
          Stream vs Stream Comparator
        </h2>
        <p className="rc-panel__meta">
          Pick any two processed streams in this scope and compare the key drivers side-by-side.
        </p>
      </div>
      {compareStreamAId && compareStreamBId && compareStreamAId === compareStreamBId ? (
        <Alert severity="warning">
          Comparing the same stream in A and B. Select a different B stream for meaningful deltas.
        </Alert>
      ) : null}

      <div className="spd-compare__pick">
        <FormControl size="small">
          <InputLabel>Stream A</InputLabel>
          <Select
            value={compareStreamAId}
            label="Stream A"
            onChange={(e) => setCompareStreamAId(String(e.target.value))}
          >
            {streamOptions.map((option) => (
              <MenuItem key={`a-${option.id}`} value={option.id}>{option.label}</MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl size="small">
          <InputLabel>Stream B</InputLabel>
          <Select
            value={compareStreamBId}
            label="Stream B"
            onChange={(e) => setCompareStreamBId(String(e.target.value))}
          >
            {streamOptions.map((option) => (
              <MenuItem key={`b-${option.id}`} value={option.id}>{option.label}</MenuItem>
            ))}
          </Select>
        </FormControl>

        <Button
          variant="outlined"
          startIcon={<SwapIcon size={18} />}
          disabled={!compareStreamAId || !compareStreamBId}
          onClick={() => {
            setCompareStreamAId(compareStreamBId);
            setCompareStreamBId(compareStreamAId);
          }}
        >
          Swap A/B
        </Button>
      </div>

      <div className="spd-streams">
        <div className="spd-stream" data-side="a">
          <span className="spd-stream__who">
            {compareStreamA?.streamer?.displayName || 'Stream A'}
          </span>
          <span className="spd-stream__title">
            {compareStreamA ? `${compareStreamA.breakCategory?.displayName || '—'} · ${compareStreamA.breakSubcategory?.displayName || '—'} · ${formatDatePretty(compareStreamA.streamDate || compareStreamA.uploadedAt)}` : 'No stream selected'}
          </span>
          <span className="spd-stream__meta">
            Stream #{compareStreamA?.id || '—'} · File: {compareStreamA?.fileName || '—'}
          </span>
        </div>

        <div className="spd-stream" data-side="b">
          <span className="spd-stream__who">
            {compareStreamB?.streamer?.displayName || 'Stream B'}
          </span>
          <span className="spd-stream__title">
            {compareStreamB ? `${compareStreamB.breakCategory?.displayName || '—'} · ${compareStreamB.breakSubcategory?.displayName || '—'} · ${formatDatePretty(compareStreamB.streamDate || compareStreamB.uploadedAt)}` : 'No stream selected'}
          </span>
          <span className="spd-stream__meta">
            Stream #{compareStreamB?.id || '—'} · File: {compareStreamB?.fileName || '—'}
          </span>
        </div>
      </div>

      <div className="spd-highlights">
        {comparisonHighlights.map((item) => (
          <div key={item.key} className="spd-highlight">
            <span className="rc-stat__label spd-highlight__label">
              {item.title}
            </span>
            <span className="spd-highlight__value" data-delta={deltaTone(item.delta, item.inverseGood)}>
              {item.value}
            </span>
          </div>
        ))}
      </div>

      <div className="rc-table-wrap spd-inset-table">
        <table className="rc-table rc-table--stack spd-table spd-stack spd-compare__table">
          <thead>
            <tr>
              <th>Metric</th>
              <th className="rc-num">
                <Tooltip title={streamMetricHeaderLabel(compareStreamA)}>
                  <span className="spd-col-label" data-side="a">
                    {streamMetricHeaderLabel(compareStreamA)}
                  </span>
                </Tooltip>
              </th>
              <th className="rc-num">
                <Tooltip title={streamMetricHeaderLabel(compareStreamB)}>
                  <span className="spd-col-label" data-side="b">
                    {streamMetricHeaderLabel(compareStreamB)}
                  </span>
                </Tooltip>
              </th>
              <th className="rc-num">A - B</th>
            </tr>
          </thead>
          <tbody>
            {comparisonSections.map((group) => (
              <Fragment key={`group-${group.section}`}>
                <tr key={`section-${group.section}`} className="spd-section-row">
                  <td colSpan={4}>
                    <span className="rc-label">{group.section}</span>
                  </td>
                </tr>
                {group.rows.map((row) => {
                  const delta = toNumber(row.a, 0) - toNumber(row.b, 0);
                  const deltaValue = row.format === formatPct ? formatDeltaPct(delta) : formatDeltaMoney(delta);
                  const fallbackDelta = row.format !== formatPct && row.format !== formatMoney
                    ? `${delta >= 0 ? '+' : ''}${formatNumber(delta, 1)}`
                    : deltaValue;
                  const defaultDelta = row.format === formatPct || row.format === formatMoney ? deltaValue : fallbackDelta;
                  const displayDelta = typeof row.deltaFormatter === 'function' ? row.deltaFormatter(delta) : defaultDelta;
                  const aBetter = Math.abs(delta) > 0.0001 ? (row.inverseGood ? delta < 0 : delta > 0) : null;
                  const bBetter = aBetter === null ? null : !aBetter;

                  return (
                    <tr key={row.key}>
                      <td className="spd-metric">{row.label}</td>
                      <td className="rc-num spd-value" data-label="Stream A" data-better={aBetter ? 'true' : undefined}>
                        <span className="spd-pair">{row.format(row.a)}</span>
                      </td>
                      <td className="rc-num spd-value" data-label="Stream B" data-better={bBetter ? 'true' : undefined}>
                        <span className="spd-pair">{row.format(row.b)}</span>
                      </td>
                      <td className="rc-num spd-value" data-label="A - B" data-delta={deltaTone(delta, row.inverseGood)}>
                        {displayDelta}
                      </td>
                    </tr>
                  );
                })}
              </Fragment>
            ))}
            {comparisonSections.length === 0 ? (
              <tr>
                <td colSpan={4} className="spd-empty-cell">
                  Choose two streams to compare. This table only uses processed paid-auction metrics.
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>
    </section>
  );

  const peerTable = (
    <section className="rc-panel spd-table-panel">
      <div className="spd-panel-head">
        <h2 className="rc-panel__title">
          Latest Stream Comparison (per streamer)
        </h2>
        <p className="rc-panel__meta">
          Each row is the latest processed stream for that streamer in the selected scope.
        </p>
      </div>
      <div className="rc-table-wrap">
        <table className="rc-table rc-table--stack spd-table spd-stack">
          <thead>
            <tr>
              <th>Streamer</th>
              <th>Latest Stream</th>
              <th className="rc-num">Rev/hr</th>
              <th className="rc-num">Δ Own</th>
              <th className="rc-num">Under £10</th>
              <th className="rc-num">Δ Own</th>
              <th className="rc-num">£20+</th>
              <th className="rc-num">Paid</th>
              <th className="rc-num">Med Gap</th>
              <th className="rc-num">Trim Gap</th>
              {isAuctionCardCategory ? (
                <>
                  <th className="rc-num">{primaryCardPluralLabel} Ran</th>
                  <th className="rc-num">Avg £/{primaryCardSingularLabel}</th>
                  <th className="rc-num">Off-screen Rev</th>
                </>
              ) : (
                <>
                  <th className="rc-num">Break Dur</th>
                  <th className="rc-num">Reset</th>
                  <th className="rc-num">Collapse</th>
                </>
              )}
              <th>Read</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {peerAverage ? (
              <tr data-row="reference">
                <td className="spd-strong">Peer Avg</td>
                <td className="spd-soft spd-wide" data-label="Latest Stream">Latest-per-streamer mean</td>
                <td className="rc-num" data-label="Rev/hr">{formatMoney(peerAverage.revenuePerHour)}</td>
                <td className="rc-num spd-soft" data-label="Δ Own">—</td>
                <td className="rc-num" data-label="Under £10">{formatPct(peerAverage.under10)}</td>
                <td className="rc-num spd-soft" data-label="Δ Own">—</td>
                <td className="rc-num" data-label="£20+">{formatPct(peerAverage.atLeast20)}</td>
                <td className="rc-num" data-label="Paid">{formatNumber(peerAverage.paidAuctionCount, 0)}</td>
                <td className="rc-num" data-label="Med Gap">{formatSeconds(peerAverage.medianGapSeconds)}</td>
                <td className="rc-num" data-label="Trim Gap">{formatSeconds(peerAverage.trimmedMeanGapSeconds)}</td>
                {isAuctionCardCategory ? (
                  <>
                    <td className="rc-num" data-label={`${primaryCardPluralLabel} Ran`}>{formatNumber(isSingleOnlyCategory ? peerAverage.singleRuns : peerAverage.gradedRuns, 1)}</td>
                    <td className="rc-num" data-label={`Avg £/${primaryCardSingularLabel}`}>{formatMoney(isSingleOnlyCategory ? peerAverage.singleAvgRevenuePerRun : peerAverage.gradedAvgRevenuePerRun)}</td>
                    <td className="rc-num" data-label="Off-screen Rev">{formatPct(peerAverage.offScreenRevenueShare)}</td>
                  </>
                ) : (
                  <>
                    <td className="rc-num" data-label="Break Dur">{formatSeconds(peerAverage.avgBreakDurationSeconds)}</td>
                    <td className="rc-num" data-label="Reset">{formatSeconds(peerAverage.medianResetSeconds)}</td>
                    <td className="rc-num" data-label="Collapse">{formatNumber(peerAverage.collapseBreaks, 1)}</td>
                  </>
                )}
                <td className="spd-soft spd-full" data-label="Read">Reference row</td>
                <td />
              </tr>
            ) : null}

            {peerRows.map((row) => {
              const stream = row.stream;
              const m = stream.metrics;
              const under10 = m?.price_bands_json?.under10;
              const atLeast20 = m?.price_bands_json?.atLeast20;
              const collapse = m?.break_summary_json?.collapseBreaks;
              const auctionSummary = m?.break_summary_json?.auctionSummary || {};
              const primaryOnScreen = getPrimaryOnScreenSummary(m, selectedCategoryKey);
              const offScreenRevenueShare = auctionSummary.offScreenRevenueShare;

              let keyRead = 'Balanced';
              if (isAuctionCardCategory) {
                if (Number(offScreenRevenueShare) > 0.2) keyRead = 'High off-screen/BIN share';
                else if (Number(primaryOnScreen.avgRevenuePerRun || 0) < 12) keyRead = `Weak ${primaryCardSingularLabel.toLowerCase()} floor`;
                else if (Number(primaryOnScreen.avgRevenuePerRun || 0) >= 20) keyRead = `Strong ${primaryCardSingularLabel.toLowerCase()} pricing`;
                else if (Number(row.deltaOwn?.revenuePerHour || 0) > 150) keyRead = 'Improving vs own trend';
              } else {
                if (Number(under10) > 0.5 && Number(atLeast20) < 0.15) keyRead = 'Price floor issue';
                else if (Number(collapse) >= 2) keyRead = 'Break collapse risk';
                else if (Number(atLeast20) > 0.35 && Number(under10) < 0.15) keyRead = 'Strong premium clears';
                else if (Number(row.deltaOwn?.revenuePerHour || 0) > 150) keyRead = 'Improving vs own trend';
              }

              return (
                <tr key={stream.id}>
                  <td className="spd-strong">
                    {stream.streamer?.displayName || 'Unknown'}
                  </td>
                  <td className="spd-soft spd-nowrap spd-wide" data-label="Latest Stream">
                    <Tooltip
                      title={
                        <Box sx={{ p: 0.5 }}>
                          <Typography sx={{ fontWeight: 700 }}>Stream #{stream.id}</Typography>
                          <Typography sx={{ opacity: 0.85 }}>Set: {stream.breakSubcategory?.displayName || '—'}</Typography>
                          <Typography sx={{ opacity: 0.85 }}>Date: {formatDate(stream.streamDate)}</Typography>
                          <Typography sx={{ opacity: 0.85 }}>Saved: {formatDateTime(stream.uploadedAt)}</Typography>
                          <Typography sx={{ opacity: 0.85 }}>File: {stream.fileName}</Typography>
                        </Box>
                      }
                    >
                      <span>#{stream.id} · {formatDate(stream.streamDate || stream.uploadedAt)}</span>
                    </Tooltip>
                  </td>
                  <td className="rc-num" data-label="Rev/hr">{formatMoney(m?.revenue_per_hour)}</td>
                  <td className="rc-num" data-label="Δ Own" data-delta={deltaTone(row.deltaOwn?.revenuePerHour)}>
                    {row.deltaOwn ? formatDeltaMoney(row.deltaOwn.revenuePerHour) : '—'}
                  </td>
                  <td className="rc-num" data-label="Under £10">
                    {m ? (
                      <Chip
                        size="small"
                        label={formatPct(under10)}
                        color={Number(under10) > 0.4 ? 'error' : 'success'}
                      />
                    ) : '—'}
                  </td>
                  <td className="rc-num" data-label="Δ Own" data-delta={deltaTone(row.deltaOwn?.under10, true)}>
                    {row.deltaOwn ? formatDeltaPct(row.deltaOwn.under10) : '—'}
                  </td>
                  <td className="rc-num" data-label="£20+">
                    {m ? (
                      <Chip
                        size="small"
                        label={formatPct(atLeast20)}
                        color={Number(atLeast20) < 0.12 ? 'warning' : 'default'}
                      />
                    ) : '—'}
                  </td>
                  <td className="rc-num" data-label="Paid">{formatNumber(m?.paid_auction_count, 0)}</td>
                  <td className="rc-num" data-label="Med Gap">{formatSeconds(m?.median_gap_seconds)}</td>
                  <td className="rc-num" data-label="Trim Gap">{formatSeconds(m?.trimmed_mean_gap_seconds)}</td>
                  {isAuctionCardCategory ? (
                    <>
                      <td className="rc-num" data-label={`${primaryCardPluralLabel} Ran`}>{formatNumber(primaryOnScreen.estimatedRuns, 0)}</td>
                      <td className="rc-num" data-label={`Avg £/${primaryCardSingularLabel}`}>{formatMoney(primaryOnScreen.avgRevenuePerRun)}</td>
                      <td className="rc-num" data-label="Off-screen Rev">{formatPct(offScreenRevenueShare)}</td>
                    </>
                  ) : (
                    <>
                      <td className="rc-num" data-label="Break Dur">{formatSeconds(m?.break_summary_json?.avgBreakDurationSeconds)}</td>
                      <td className="rc-num" data-label="Reset">{formatSeconds(m?.break_summary_json?.medianResetSeconds)}</td>
                      <td className="rc-num" data-label="Collapse">{formatNumber(collapse, 0)}</td>
                    </>
                  )}
                  <td className="spd-read spd-full" data-label="Read">{keyRead}</td>
                  <td className="rc-num">
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => openDetail(stream)}
                    >
                      Details
                    </Button>
                  </td>
                </tr>
              );
            })}

            {!isLoadingReports && peerRows.length === 0 ? (
              <tr>
                <td colSpan={15} className="spd-empty-cell">
                  No processed streams found for this filter yet.
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>
    </section>
  );

  const allStreamsPanel = (
    <section className="rc-panel spd-table-panel">
      <div className="spd-panel-head">
        <h2 className="rc-panel__title">
          All Streams Explorer
        </h2>
        <p className="rc-panel__meta">
          Every processed stream in this filter. Use rows as comparison A or B.
          {canDeleteStreams ? ' Admins can also permanently delete mistaken/duplicate uploads.' : ''}
        </p>
      </div>

      <div className="rc-table-wrap">
        <table className="rc-table rc-table--stack spd-table spd-stack spd-explorer">
          <thead>
            <tr>
              <th>Tag</th>
              <th>Stream</th>
              <th>Streamer</th>
              <th>Set</th>
              <th className="rc-num">Rev/hr</th>
              <th className="rc-num">Under £10</th>
              <th className="rc-num">£20+</th>
              <th className="rc-num">Paid</th>
              <th className="rc-num">Auctions/hr</th>
              <th className="rc-num">Runtime</th>
              <th className="rc-num">Med Gap</th>
              {isAuctionCardCategory ? (
                <>
                  <th className="rc-num">{primaryCardPluralLabel} Ran</th>
                  <th className="rc-num">Avg £/{primaryCardSingularLabel}</th>
                  <th className="rc-num">Off-screen Rev</th>
                </>
              ) : (
                <>
                  <th className="rc-num">Break Dur</th>
                  <th className="rc-num">Reset</th>
                </>
              )}
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {sortedReports.map((stream) => {
              const id = String(stream.id);
              const isA = compareStreamAId === id;
              const isB = compareStreamBId === id;
              const auctionSummary = stream.metrics?.break_summary_json?.auctionSummary || {};
              const primaryOnScreen = getPrimaryOnScreenSummary(stream.metrics, selectedCategoryKey);

              return (
                <tr
                  key={`all-${id}`}
                  data-pick={isA ? 'a' : isB ? 'b' : undefined}
                >
                  <td className="spd-tag">
                    {isA ? (
                      <Chip size="small" label="A" color="primary" />
                    ) : null}
                    {isB ? (
                      <Chip size="small" label="B" className="rc-badge--strong" />
                    ) : null}
                    {!isA && !isB ? <span className="spd-soft">—</span> : null}
                  </td>
                  <td className="spd-strong spd-nowrap spd-x-stream">
                    #{stream.id} · {formatDate(stream.streamDate || stream.uploadedAt)}
                  </td>
                  <td className="spd-x-who" data-label="Streamer">{stream.streamer?.displayName || 'Unknown'}</td>
                  <td className="spd-x-who" data-label="Set">{stream.breakSubcategory?.displayName || 'Unspecified'}</td>
                  <td className="rc-num" data-label="Rev/hr">{formatMoney(stream.metrics?.revenue_per_hour)}</td>
                  <td className="rc-num" data-label="Under £10">{formatPct(stream.metrics?.price_bands_json?.under10)}</td>
                  <td className="rc-num" data-label="£20+">{formatPct(stream.metrics?.price_bands_json?.atLeast20)}</td>
                  <td className="rc-num" data-label="Paid">{formatNumber(stream.metrics?.paid_auction_count, 0)}</td>
                  <td className="rc-num" data-label="Auctions/hr">{formatNumber(stream.metrics?.auctions_per_hour, 1)}</td>
                  <td className="rc-num" data-label="Runtime">{formatSeconds(stream.metrics?.runtime_seconds)}</td>
                  <td className="rc-num" data-label="Med Gap">{formatSeconds(stream.metrics?.median_gap_seconds)}</td>
                  {isAuctionCardCategory ? (
                    <>
                      <td className="rc-num" data-label={`${primaryCardPluralLabel} Ran`}>{formatNumber(primaryOnScreen.estimatedRuns, 0)}</td>
                      <td className="rc-num" data-label={`Avg £/${primaryCardSingularLabel}`}>{formatMoney(primaryOnScreen.avgRevenuePerRun)}</td>
                      <td className="rc-num" data-label="Off-screen Rev">{formatPct(auctionSummary.offScreenRevenueShare)}</td>
                    </>
                  ) : (
                    <>
                      <td className="rc-num" data-label="Break Dur">{formatSeconds(stream.metrics?.break_summary_json?.avgBreakDurationSeconds)}</td>
                      <td className="rc-num" data-label="Reset">{formatSeconds(stream.metrics?.break_summary_json?.medianResetSeconds)}</td>
                    </>
                  )}
                  <td>
                    <div className="spd-actions">
                      <Button
                        size="small"
                        variant="outlined"
                        data-active={isA ? 'true' : undefined}
                        onClick={() => setCompareStreamAId(id)}
                      >
                        Use as A
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        data-active={isB ? 'true' : undefined}
                        onClick={() => setCompareStreamBId(id)}
                      >
                        Use as B
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => openDetail(stream)}
                      >
                        Details
                      </Button>
                      {canDeleteStreams ? (
                        <Button
                          size="small"
                          variant="outlined"
                          color="error"
                          disabled={isDeletingStreamId === id}
                          onClick={() => deleteStream(stream)}
                        >
                          {isDeletingStreamId === id ? 'Deleting...' : 'Delete'}
                        </Button>
                      ) : null}
                    </div>
                  </td>
                </tr>
              );
            })}
            {!isLoadingReports && sortedReports.length === 0 ? (
              <tr>
                <td colSpan={isAuctionCardCategory ? 15 : 14} className="spd-empty-cell">
                  No streams found for this filter yet.
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>
    </section>
  );

  const focusAuctionSummary = focusCurrent?.metrics?.break_summary_json?.auctionSummary || {};
  const focusPrimarySummary = getPrimaryOnScreenSummary(focusCurrent?.metrics, selectedCategoryKey);
  const detailCategoryKey = toSlug(detailStream?.breakCategory?.slug || detailStream?.breakCategory?.displayName || '');
  const detailIsAuctionCategory = isAuctionCardCategoryKey(detailCategoryKey);
  const detailIsSingleOnlyCategory = isSingleOnlyCategoryKey(detailCategoryKey);
  const detailPrimaryCardSingularLabel = detailIsSingleOnlyCategory ? 'Single' : 'Slab';
  const detailPrimaryCardPluralLabel = detailIsSingleOnlyCategory ? 'Singles' : 'Slabs';
  const detailAuctionSummary = detailStream?.metrics?.break_summary_json?.auctionSummary || {};
  const detailCleaningSummary = detailStream?.metrics?.break_summary_json?.cleaningSummary || {};
  const detailSlabRows = useMemo(() => {
    const primary = detailIsSingleOnlyCategory ? (detailAuctionSummary?.singleOnScreen || {}) : (detailAuctionSummary?.gradedOnScreen || {});
    const single = detailAuctionSummary?.singleOnScreen || {};
    const off = detailAuctionSummary?.nonOnScreen || {};

    const rows = [
      { label: `${detailPrimaryCardSingularLabel} on-screen auctions`, value: formatNumber(primary.auctionCount, 0) },
      { label: `${detailPrimaryCardPluralLabel} ran (#)`, value: formatNumber(primary.estimatedRuns, 0) },
      { label: `Avg revenue per ${detailPrimaryCardSingularLabel.toLowerCase()} run`, value: formatMoney(primary.avgRevenuePerRun) },
      { label: `Avg revenue per ${detailPrimaryCardSingularLabel.toLowerCase()} auction`, value: formatMoney(primary.avgRevenuePerAuction) },
      { label: `${detailPrimaryCardSingularLabel} median auction gap`, value: formatSeconds(primary.medianGapSeconds) },
      { label: `${detailPrimaryCardSingularLabel} auctions per hour`, value: formatNumber(primary.auctionsPerHour, 1) },
    ];

    if (!detailIsSingleOnlyCategory && Number(single.auctionCount || 0) > 0) {
      rows.push(
        { label: 'Single on-screen auctions', value: formatNumber(single.auctionCount, 0) },
        { label: 'Single-card runs (#)', value: formatNumber(single.estimatedRuns, 0) },
        { label: 'Avg revenue per single-card run', value: formatMoney(single.avgRevenuePerRun) },
      );
    }

    rows.push(
      { label: 'Off-screen/BIN paid auctions', value: formatNumber(off.auctionCount, 0) },
      { label: 'Off-screen/BIN paid revenue', value: formatMoney(off.revenue) },
      { label: 'Off-screen revenue share', value: formatPct(detailAuctionSummary?.offScreenRevenueShare) },
      { label: 'Giveaways removed (£0)', value: formatNumber(detailCleaningSummary?.giveawaysRemoved, 0) },
      { label: 'Failed/cancelled removed', value: formatNumber(detailCleaningSummary?.cancelledRemoved, 0) },
      { label: 'Raw CSV rows', value: formatNumber(detailCleaningSummary?.totalRows, 0) },
    );

    return rows;
  }, [detailAuctionSummary, detailCleaningSummary, detailIsSingleOnlyCategory, detailPrimaryCardPluralLabel, detailPrimaryCardSingularLabel]);

  const ownHistoryPanel = (
    <section className="rc-panel spd-history">
      <div className="spd-panel-head">
        <h2 className="rc-panel__title">
          Own History: {focusCurrent?.streamer?.displayName || '—'}
        </h2>
        <p className="rc-panel__meta">
          Latest stream vs own baseline, plus recent history in this scope.
        </p>
      </div>

      <div className="spd-kpis">
        <div className="spd-kpi">
          <span className="rc-stat__label spd-kpi__label">Rev/hr (current vs baseline)</span>
          <span className="spd-kpi__value">
            {formatMoney(focusCurrent?.metrics?.revenue_per_hour)}
          </span>
          <span className="spd-kpi__delta" data-delta={deltaTone(Number(focusCurrent?.metrics?.revenue_per_hour || 0) - Number(focusBaseline?.revenuePerHour || NaN))}>
            {focusBaseline ? formatDeltaMoney(Number(focusCurrent?.metrics?.revenue_per_hour || 0) - Number(focusBaseline.revenuePerHour || 0)) : 'No baseline yet'}
          </span>
        </div>
        {isAuctionCardCategory ? (
          <>
            <div className="spd-kpi">
              <span className="rc-stat__label spd-kpi__label">Avg £/{primaryCardSingularLabel} (current vs baseline)</span>
              <span className="spd-kpi__value">
                {formatMoney(focusPrimarySummary?.avgRevenuePerRun)}
              </span>
              <span className="spd-kpi__delta" data-delta={deltaTone(Number(focusPrimarySummary?.avgRevenuePerRun || 0) - Number(isSingleOnlyCategory ? focusBaseline?.singleAvgRevenuePerRun : focusBaseline?.gradedAvgRevenuePerRun || NaN))}>
                {focusBaseline
                  ? formatDeltaMoney(
                    Number(focusPrimarySummary?.avgRevenuePerRun || 0)
                    - Number(isSingleOnlyCategory ? focusBaseline.singleAvgRevenuePerRun : focusBaseline.gradedAvgRevenuePerRun || 0)
                  )
                  : 'No baseline yet'}
              </span>
            </div>
            <div className="spd-kpi">
              <span className="rc-stat__label spd-kpi__label">Off-screen Rev% (current vs baseline)</span>
              <span className="spd-kpi__value">
                {formatPct(focusAuctionSummary?.offScreenRevenueShare)}
              </span>
              <span className="spd-kpi__delta" data-delta={deltaTone(Number(focusAuctionSummary?.offScreenRevenueShare || 0) - Number(focusBaseline?.offScreenRevenueShare || NaN), true)}>
                {focusBaseline ? formatDeltaPct(Number(focusAuctionSummary?.offScreenRevenueShare || 0) - Number(focusBaseline.offScreenRevenueShare || 0)) : 'No baseline yet'}
              </span>
            </div>
          </>
        ) : (
          <>
            <div className="spd-kpi">
              <span className="rc-stat__label spd-kpi__label">Under £10 (current vs baseline)</span>
              <span className="spd-kpi__value">
                {formatPct(focusCurrent?.metrics?.price_bands_json?.under10)}
              </span>
              <span className="spd-kpi__delta" data-delta={deltaTone(Number(focusCurrent?.metrics?.price_bands_json?.under10 || 0) - Number(focusBaseline?.under10 || NaN), true)}>
                {focusBaseline ? formatDeltaPct(Number(focusCurrent?.metrics?.price_bands_json?.under10 || 0) - Number(focusBaseline.under10 || 0)) : 'No baseline yet'}
              </span>
            </div>
            <div className="spd-kpi">
              <span className="rc-stat__label spd-kpi__label">£20+ (current vs baseline)</span>
              <span className="spd-kpi__value">
                {formatPct(focusCurrent?.metrics?.price_bands_json?.atLeast20)}
              </span>
              <span className="spd-kpi__delta" data-delta={deltaTone(Number(focusCurrent?.metrics?.price_bands_json?.atLeast20 || 0) - Number(focusBaseline?.atLeast20 || NaN))}>
                {focusBaseline ? formatDeltaPct(Number(focusCurrent?.metrics?.price_bands_json?.atLeast20 || 0) - Number(focusBaseline.atLeast20 || 0)) : 'No baseline yet'}
              </span>
            </div>
          </>
        )}
      </div>

      <div className="rc-table-wrap spd-inset-table">
        <table className="rc-table rc-table--stack spd-table spd-stack">
          <thead>
            <tr>
              <th>Stream</th>
              <th>Set</th>
              <th className="rc-num">Rev/hr</th>
              <th className="rc-num">Auctions/hr</th>
              {isAuctionCardCategory ? (
                <>
                  <th className="rc-num">{primaryCardPluralLabel} Ran</th>
                  <th className="rc-num">Avg £/{primaryCardSingularLabel}</th>
                  <th className="rc-num">Off-screen Rev</th>
                  {includeSecondarySingleRows ? (
                    <th className="rc-num">Single Runs</th>
                  ) : null}
                </>
              ) : (
                <>
                  <th className="rc-num">Under £10</th>
                  <th className="rc-num">£20+</th>
                  <th className="rc-num">Collapse</th>
                </>
              )}
            </tr>
          </thead>
          <tbody>
            {focusHistory.slice(0, 10).map((r) => {
              const auction = r.metrics?.break_summary_json?.auctionSummary || {};
              const primary = getPrimaryOnScreenSummary(r.metrics, selectedCategoryKey);
              const single = auction.singleOnScreen || {};

              return (
                <tr key={r.id}>
                  <td className="spd-strong spd-nowrap">
                    #{r.id} · {formatDate(r.streamDate || r.uploadedAt)}
                  </td>
                  <td className="spd-soft" data-label="Set">
                    {r.breakSubcategory?.displayName || '—'}
                  </td>
                  <td className="rc-num" data-label="Rev/hr">{formatMoney(r.metrics?.revenue_per_hour)}</td>
                  <td className="rc-num" data-label="Auctions/hr">{formatNumber(r.metrics?.auctions_per_hour, 1)}</td>
                  {isAuctionCardCategory ? (
                    <>
                      <td className="rc-num" data-label={`${primaryCardPluralLabel} Ran`}>{formatNumber(primary.estimatedRuns, 0)}</td>
                      <td className="rc-num" data-label={`Avg £/${primaryCardSingularLabel}`}>{formatMoney(primary.avgRevenuePerRun)}</td>
                      <td className="rc-num" data-label="Off-screen Rev">{formatPct(auction.offScreenRevenueShare)}</td>
                      {includeSecondarySingleRows ? (
                        <td className="rc-num" data-label="Single Runs">{formatNumber(single.estimatedRuns, 0)}</td>
                      ) : null}
                    </>
                  ) : (
                    <>
                      <td className="rc-num" data-label="Under £10">{formatPct(r.metrics?.price_bands_json?.under10)}</td>
                      <td className="rc-num" data-label="£20+">{formatPct(r.metrics?.price_bands_json?.atLeast20)}</td>
                      <td className="rc-num" data-label="Collapse">{formatNumber(r.metrics?.break_summary_json?.collapseBreaks, 0)}</td>
                    </>
                  )}
                </tr>
              );
            })}
            {focusHistory.length === 0 ? (
              <tr>
                <td colSpan={isAuctionCardCategory ? (includeSecondarySingleRows ? 8 : 7) : 7} className="spd-empty-cell">
                  No history for this streamer in the current scope.
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>
    </section>
  );

  const setMatrixPanel = setScope === 'all' ? (
    <section className="rc-panel spd-table-panel">
      <div className="spd-panel-head">
        <h2 className="rc-panel__title">
          Cross-Set Comparison (All subcategories)
        </h2>
        <p className="rc-panel__meta">
          {isAuctionCardCategory
            ? `Each cell uses the latest stream in that set: Avg £/${primaryCardSingularLabel} | Off-screen Rev% | Rev/hr.`
            : 'Each cell uses the latest stream in that set: Rev/hr | Under £10 | £20+.'}
        </p>
      </div>

      <div className="rc-table-wrap">
        <table className="rc-table rc-table--stack spd-table spd-stack spd-matrix">
          <thead>
            <tr>
              <th>Set</th>
              <th>
                {isAuctionCardCategory ? `Best £/${primaryCardSingularLabel}` : 'Best Rev/hr'}
              </th>
              <th>
                {isAuctionCardCategory ? 'Highest Off-screen Rev' : 'Highest Under £10'}
              </th>
              {availableStreamerOptions.map((s) => (
                <th key={s.id}>
                  {s.name}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {setMatrixRows.map((row) => (
              <tr key={row.setId}>
                <td className="spd-strong">{row.setName}</td>
                <td className="spd-nowrap spd-half" data-label={isAuctionCardCategory ? `Best £/${primaryCardSingularLabel}` : 'Best Rev/hr'} data-delta="good">
                  {row.best
                    ? `${row.best.streamerName} · ${isAuctionCardCategory ? formatMoney(row.best.primaryAvgRevenuePerRun) : formatMoney(row.best.revenuePerHour)}`
                    : '—'}
                </td>
                <td className="spd-nowrap spd-half" data-label={isAuctionCardCategory ? 'Highest Off-screen Rev' : 'Highest Under £10'} data-delta="bad">
                  {row.highestRisk
                    ? `${row.highestRisk.streamerName} · ${isAuctionCardCategory ? formatPct(row.highestRisk.offScreenRevenueShare) : formatPct(row.highestRisk.under10)}`
                    : '—'}
                </td>
                {row.cells.map((cell) => (
                  <td key={cell.streamerId} className="spd-matrix__cell" data-label={cell.streamerName}>
                    {cell.stream ? (
                      <div className="spd-cell">
                        <span className="spd-cell__value">
                          {isAuctionCardCategory ? formatMoney(cell.primaryAvgRevenuePerRun) : formatMoney(cell.revenuePerHour)}
                        </span>
                        {isAuctionCardCategory ? (
                          <span className="spd-cell__meta">
                            Off-screen {formatPct(cell.offScreenRevenueShare)} · Rev/hr {formatMoney(cell.revenuePerHour)}
                          </span>
                        ) : (
                          <span className="spd-cell__meta">
                            U10 {formatPct(cell.under10)} · 20+ {formatPct(cell.atLeast20)}
                          </span>
                        )}
                        <span className="spd-cell__id">
                          Stream #{cell.stream.id}
                        </span>
                      </div>
                    ) : (
                      <span className="spd-soft">—</span>
                    )}
                  </td>
                ))}
              </tr>
            ))}
            {setMatrixRows.length === 0 ? (
              <tr>
                <td colSpan={3 + availableStreamerOptions.length} className="spd-empty-cell">
                  No cross-set data found yet for this category.
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>
    </section>
  ) : null;

  return (
    <div className="rc-page spd-page">
      {header}
      {isResolvingAccess ? (
        <section className="rc-panel spd-state">
          <CircularProgress size={20} />
          <p>
            Checking stream performance access...
          </p>
        </section>
      ) : !canViewPerformance ? (
        <Alert severity="warning" className="spd-state">
          <strong>
            Stream Performance Access Required
          </strong>
          <span>
            Your account is signed in, but it is not currently approved for this tool.
            Ask Randolph to add your email in Stream Performance Access Manager.
          </span>
        </Alert>
      ) : (
        <>
          {filterBar}
          {accessManagerPanel}

          {error ? (
            <Alert severity="error">
              <strong>Error</strong>
              <span>{error}</span>
            </Alert>
          ) : null}

          {successMessage ? (
            <Alert severity="success">
              <strong>Success</strong>
              <span>{successMessage}</span>
            </Alert>
          ) : null}

          {insightsRow}
          {specificComparisonPanel}
          {peerTable}
          {allStreamsPanel}
          {ownHistoryPanel}
          {setMatrixPanel}
        </>
      )}

      <Dialog
        open={detailOpen}
        onClose={() => setDetailOpen(false)}
        maxWidth="md"
      >
        <div className="spd-detail">
          <DialogTitle>
            <h2 className="spd-detail__title">
              {detailStream?.streamer?.displayName || 'Stream'} · {detailIsAuctionCategory ? 'Auction Detail' : 'Break Detail'}
            </h2>
          </DialogTitle>
          <DialogContent>
            <p className="spd-detail__meta">
              Stream #{detailStream?.id} · {detailStream?.breakCategory?.displayName} / {detailStream?.breakSubcategory?.displayName}
            </p>
            <p className="spd-detail__meta">
              Stream date {formatDate(detailStream?.streamDate)} · Saved {formatDateTime(detailStream?.uploadedAt)}
            </p>
            {detailStream?.fileName ? (
              <p className="spd-detail__file">File: {detailStream.fileName}</p>
            ) : null}

            <Divider sx={{ my: 2 }} />

            {isLoadingDetail ? (
              <div className="spd-detail__loading">
                <CircularProgress />
              </div>
            ) : (
              detailIsAuctionCategory ? (
                <div className="rc-table-wrap spd-inset-table">
                  <table className="rc-table spd-table">
                    <thead>
                      <tr>
                        <th>Metric</th>
                        <th className="rc-num">Value</th>
                      </tr>
                    </thead>
                    <tbody>
                      {detailSlabRows.map((row) => (
                        <tr key={row.label}>
                          <td className="spd-metric">{row.label}</td>
                          <td className="rc-num spd-value">{row.value}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="rc-table-wrap spd-inset-table">
                  <table className="rc-table rc-table--stack spd-table spd-stack">
                    <thead>
                      <tr>
                        <th>Break</th>
                        <th className="rc-num">Spots</th>
                        <th className="rc-num">Revenue</th>
                        <th className="rc-num">£/Spot</th>
                        <th className="rc-num">Duration</th>
                        <th className="rc-num">Reset</th>
                      </tr>
                    </thead>
                    <tbody>
                      {detailBreaks.map((b) => (
                        <tr key={b.break_no} data-collapse={b.is_collapse ? 'true' : undefined}>
                          <td className="spd-strong" data-label="Break">{b.break_no}</td>
                          <td className="rc-num" data-label="Spots">{b.paid_spot_count}</td>
                          <td className="rc-num" data-label="Revenue">{formatMoney(b.total_revenue)}</td>
                          <td className="rc-num" data-label="£/Spot">{formatMoney(b.revenue_per_spot)}</td>
                          <td className="rc-num spd-soft" data-label="Duration">{formatSeconds(b.duration_seconds)}</td>
                          <td className="rc-num spd-soft" data-label="Reset">
                            {b.reset_to_next_seconds !== null && b.reset_to_next_seconds !== undefined
                              ? formatSeconds(b.reset_to_next_seconds)
                              : '—'}
                          </td>
                        </tr>
                      ))}
                      {detailBreaks.length === 0 ? (
                        <tr>
                          <td colSpan={6} className="spd-empty-cell">
                            No break metrics available.
                          </td>
                        </tr>
                      ) : null}
                    </tbody>
                  </table>
                </div>
              )
            )}
          </DialogContent>
          <DialogActions>
            <Button
              variant="contained"
              onClick={() => setDetailOpen(false)}
            >
              Close
            </Button>
          </DialogActions>
        </div>
      </Dialog>
    </div>
  );
}
